import * as React from 'react';

/**
 * Per-person appearance: the accent colour and the UI font.
 *
 * Sits alongside `theme.tsx`, which owns light/dark and is untouched. Both are
 * device preferences in localStorage - there is no `pref_*` column on Employee
 * and adding one would mean a migration, an endpoint and a fetch-then-repaint
 * flash. If someone asks for "my colour follows me to another machine", that is
 * the change: store these two values on the account and keep localStorage as
 * the paint-time cache.
 *
 * The storage key and the shape of what it holds must stay in lockstep with the
 * inline script in index.html, which replays the last-painted values before
 * first paint. Without that replay every load flashes the default teal first.
 */

export const APPEARANCE_STORAGE_KEY = 'cfl-assets-appearance';

export type Appearance = { accent: string | null; fontId: string | null };

/* ---------- accent ---------- */

export type AccentPreset = { id: string; label: string; hex: string };

/**
 * The swatch row. Deliberately short - this is a personalisation affordance,
 * not a colour picker with presets; anyone wanting a specific colour uses the
 * custom field beside it.
 *
 * Each hex is a mid-tone that already clears 4.5:1 on the light card (5.0 to
 * 6.3), so the light variant is the colour as picked. Every one of them fails
 * on the dark card - 2.6 to 3.2 - so the dark variant is always derived; see
 * `fitToSurface` for why one hex cannot serve both themes.
 *
 * The brand teal is the exception at 4.38 on white, and it does not matter:
 * picking it stores null and the hand-tuned tokens are used untouched.
 */
const TEAL: AccentPreset = { id: 'teal', label: 'CargoFL teal', hex: '#008a5c' };

export const ACCENT_PRESETS: AccentPreset[] = [
  TEAL,
  { id: 'violet', label: 'Violet', hex: '#7c3aed' },
  { id: 'indigo', label: 'Indigo', hex: '#4f46e5' },
  { id: 'blue', label: 'Blue', hex: '#0369a1' },
  { id: 'amber', label: 'Amber', hex: '#b45309' },
  { id: 'rose', label: 'Rose', hex: '#be123c' },
];

/** What "Reset to default" restores - the hex form of `--primary` in globals.css. */
export const DEFAULT_ACCENT = TEAL.hex;

const HEX_RE = /^#[0-9a-f]{6}$/i;

/** Normalises `6D28D9`, `#6D28D9`, `  #6d28d9 ` to `#6d28d9`, or returns null
 *  when it is not a colour we are willing to write into CSS. Three-digit hex is
 *  expanded - people type `#f00`. */
export function normalizeAccent(input: string | null | undefined): string | null {
  if (!input) return null;
  let v = String(input).trim().toLowerCase();
  if (!v.startsWith('#')) v = `#${v}`;
  if (/^#[0-9a-f]{3}$/.test(v)) v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  return HEX_RE.test(v) ? v : null;
}

/**
 * Relative luminance per WCAG 2.x, used to decide what colour sits ON a filled
 * accent button. It has to be computed here rather than in CSS: `color-mix` can
 * tint a colour but cannot ask "is white readable on this yet", and
 * `contrast-color()` is too new to depend on. Without it a light accent renders
 * white-on-amber at about 2:1 and every primary button label is unreadable.
 */
export function relativeLuminance(hex: string): number {
  const v = normalizeAccent(hex) || DEFAULT_ACCENT;
  const channel = (i: number) => {
    const c = parseInt(v.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

/** Contrast ratio between two hex colours (1-21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** White or near-black, whichever is more readable on a fill of this accent.
 *  The near-black is `#111318` - the same tone `--primary-foreground` already
 *  uses in dark mode, so a light accent lands on a colour the palette owns. */
export function onAccentColor(hex: string): string {
  const dark = '#111318';
  return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, dark) ? '#ffffff' : dark;
}

/* The two card surfaces `--primary` has to stay legible against: `--card` in
   each theme (hsl(0 0% 100%) and hsl(222 16% 14%)). Retune these if those move. */
const LIGHT_SURFACE = '#ffffff';
const DARK_SURFACE = '#1e2129';
/** The AA bar for normal text. `--primary` is a text colour as often as it is a
 *  fill (links, active nav, every `text-primary`), so the large-text 3:1
 *  allowance does not apply. */
const TEXT_TARGET = 4.5;

function mixHex(a: string, b: string, t: number): string {
  const chan = (hex: string, i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  const out = [0, 1, 2].map((i) => Math.round(chan(a, i) * (1 - t) + chan(b, i) * t));
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Move a colour just far enough to be readable on a given surface. This is what
 * makes a free colour field safe to ship: the readable band differs per theme,
 * which is exactly why the shipped palette hand-writes two teals (27% lightness
 * for light, 39% for dark) instead of one.
 *
 * Neither failure is hypothetical - a mid-tone violet measures 2.8:1 as text on
 * the dark card, a bright yellow 1.5:1 on the light one, and both are colours a
 * person will genuinely pick.
 *
 * One-directional and a no-op when it can be: a colour already past the target
 * is returned untouched, so a considered choice is honoured exactly and only an
 * unreadable one moves. Mixing toward black/white preserves hue, and the target
 * is always reachable since both endpoints are the extremes.
 */
export function fitToSurface(hex: string, surface: string, target = TEXT_TARGET): string {
  if (contrastRatio(hex, surface) >= target) return hex;
  const toward = relativeLuminance(surface) > 0.5 ? '#000000' : '#ffffff';
  // 12 halvings resolves the mix to well under one 8-bit step.
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(mixHex(hex, toward, mid), surface) >= target) hi = mid;
    else lo = mid;
  }
  return mixHex(hex, toward, hi);
}

/**
 * Hex to the bare `H S% L%` triplet the tokens hold.
 *
 * Every token in globals.css is a triplet consumed as `hsl(var(--primary))`,
 * and `@theme inline` means Tailwind inlines that expression into each utility -
 * so overriding `--color-primary` would do nothing and the triplet is the only
 * value worth writing. Kept to two decimals rather than rounded to integers,
 * which would shift a colour off the contrast ratio just checked for it.
 */
export function hexToHsl(hex: string): string {
  const ch = (i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
  const r = ch(0);
  const g = ch(1);
  const b = ch(2);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d) {
    s = d / (1 - Math.abs(2 * l - 1));
    h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  return `${h.toFixed(2)} ${(s * 100).toFixed(2)}% ${(l * 100).toFixed(2)}%`;
}

/** Just the hue, for the sidebar - see `accentVars`. */
export function hexHue(hex: string): string {
  return hexToHsl(hex).split(' ')[0] ?? '0';
}

/** The per-theme pair a chosen accent resolves to, plus what sits ON each as a
 *  fill - as triplets, ready for CSS. Computed once at pick time and handed to
 *  the stylesheet, which selects between them, so flipping the theme
 *  recomputes nothing. */
export type AccentVars = {
  /** The bare hue number. The sidebar is a near-black brand-tinted surface in
   *  both themes, and globals.css retints it by substituting this hue into the
   *  rail's own saturation and lightness - so the rail follows the accent while
   *  keeping the values that make its text readable. It cannot be a `color-mix`
   *  the way the reference app does it, because every token here is a bare
   *  `H S% L%` triplet and there is nothing to mix into.
   *  A fully desaturated pick has no meaningful hue and tints the rail by
   *  whatever hue its greyscale rounds to; the rail is still near-black. */
  hue: string;
  light: string;
  onLight: string;
  dark: string;
  onDark: string;
};

export function accentVars(hex: string): AccentVars {
  const light = fitToSurface(hex, LIGHT_SURFACE);
  const dark = fitToSurface(hex, DARK_SURFACE);
  return {
    // From the pick, not a fitted variant - mixing toward black/white preserves
    // hue, so they agree, and this is the value the person actually chose.
    hue: hexHue(hex),
    light: hexToHsl(light),
    onLight: hexToHsl(onAccentColor(light)),
    dark: hexToHsl(dark),
    onDark: hexToHsl(onAccentColor(dark)),
  };
}

/* ---------- font ---------- */

export type FontOption = {
  id: string;
  label: string;
  /** The full CSS stack. Only ever a string this file wrote. */
  stack: string;
  /** The one family to probe with `document.fonts.check()`. Empty for faces
   *  that are bundled or guaranteed by the platform. */
  probe: string;
};

const UI_FALLBACK = `ui-sans-serif, system-ui, -apple-system, sans-serif`;

/**
 * The font list. All system faces plus the bundled default: nothing here is
 * downloaded, because pulling families from a font CDN would add a third-party
 * request to every page for a cosmetic preference. That is why the picker's
 * heading says "System".
 *
 * Availability is per machine, so the list is filtered at render time by
 * `isFontAvailable` - the Mac faces and the Windows ones are both in here, and
 * a machine should not be offered one it would silently render as Geist.
 */
const GEIST: FontOption = {
  id: 'default',
  label: 'Geist (default)',
  stack: `"Geist", ${UI_FALLBACK}`,
  probe: '',
};

export const FONT_OPTIONS: FontOption[] = [
  GEIST,
  { id: 'system-ui', label: 'System UI', stack: UI_FALLBACK, probe: '' },
  { id: 'arial', label: 'Arial', stack: `Arial, ${UI_FALLBACK}`, probe: 'Arial' },
  { id: 'arial-black', label: 'Arial Black', stack: `"Arial Black", ${UI_FALLBACK}`, probe: 'Arial Black' },
  { id: 'arial-narrow', label: 'Arial Narrow', stack: `"Arial Narrow", ${UI_FALLBACK}`, probe: 'Arial Narrow' },
  { id: 'avenir', label: 'Avenir Next', stack: `"Avenir Next", Avenir, ${UI_FALLBACK}`, probe: 'Avenir Next' },
  { id: 'bahnschrift', label: 'Bahnschrift', stack: `Bahnschrift, ${UI_FALLBACK}`, probe: 'Bahnschrift' },
  { id: 'baskerville', label: 'Baskerville', stack: `Baskerville, Georgia, serif`, probe: 'Baskerville' },
  { id: 'book-antiqua', label: 'Book Antiqua', stack: `"Book Antiqua", Palatino, serif`, probe: 'Book Antiqua' },
  { id: 'calibri', label: 'Calibri', stack: `Calibri, ${UI_FALLBACK}`, probe: 'Calibri' },
  { id: 'cambria', label: 'Cambria', stack: `Cambria, Georgia, serif`, probe: 'Cambria' },
  { id: 'candara', label: 'Candara', stack: `Candara, ${UI_FALLBACK}`, probe: 'Candara' },
  { id: 'consolas', label: 'Consolas', stack: `Consolas, ui-monospace, monospace`, probe: 'Consolas' },
  { id: 'constantia', label: 'Constantia', stack: `Constantia, Georgia, serif`, probe: 'Constantia' },
  { id: 'corbel', label: 'Corbel', stack: `Corbel, ${UI_FALLBACK}`, probe: 'Corbel' },
  { id: 'courier-new', label: 'Courier New', stack: `"Courier New", ui-monospace, monospace`, probe: 'Courier New' },
  { id: 'franklin-gothic', label: 'Franklin Gothic', stack: `"Franklin Gothic Medium", ${UI_FALLBACK}`, probe: 'Franklin Gothic Medium' },
  { id: 'garamond', label: 'Garamond', stack: `Garamond, Georgia, serif`, probe: 'Garamond' },
  { id: 'georgia', label: 'Georgia', stack: `Georgia, serif`, probe: 'Georgia' },
  { id: 'helvetica', label: 'Helvetica Neue', stack: `"Helvetica Neue", Helvetica, ${UI_FALLBACK}`, probe: 'Helvetica Neue' },
  { id: 'menlo', label: 'Menlo', stack: `Menlo, ui-monospace, monospace`, probe: 'Menlo' },
  { id: 'optima', label: 'Optima', stack: `Optima, ${UI_FALLBACK}`, probe: 'Optima' },
  { id: 'palatino', label: 'Palatino', stack: `"Palatino Linotype", Palatino, serif`, probe: 'Palatino Linotype' },
  { id: 'segoe-ui', label: 'Segoe UI', stack: `"Segoe UI", ${UI_FALLBACK}`, probe: 'Segoe UI' },
  { id: 'sf-pro', label: 'SF Pro Text', stack: `"SF Pro Text", -apple-system, ${UI_FALLBACK}`, probe: 'SF Pro Text' },
  { id: 'tahoma', label: 'Tahoma', stack: `Tahoma, ${UI_FALLBACK}`, probe: 'Tahoma' },
  { id: 'times-new-roman', label: 'Times New Roman', stack: `"Times New Roman", Times, serif`, probe: 'Times New Roman' },
  { id: 'trebuchet', label: 'Trebuchet MS', stack: `"Trebuchet MS", ${UI_FALLBACK}`, probe: 'Trebuchet MS' },
  { id: 'verdana', label: 'Verdana', stack: `Verdana, ${UI_FALLBACK}`, probe: 'Verdana' },
];

export const DEFAULT_FONT_ID = GEIST.id;

export function fontById(id: string | null | undefined): FontOption {
  return FONT_OPTIONS.find((f) => f.id === id) ?? GEIST;
}

/**
 * Whether this machine has the family. `document.fonts.check` is the only way
 * to ask without the Local Font Access API's permission prompt, which would be
 * wildly disproportionate for a cosmetic list.
 *
 * It is a hint, not an answer: some engines only consider webfonts here and
 * report every local family as present, so this filter removes what it can and
 * lets the rest through. The failure mode is a row that renders in Geist rather
 * than in its own face - visible, harmless, and it still selects cleanly.
 */
export function isFontAvailable(option: FontOption): boolean {
  if (!option.probe) return true;
  if (typeof document === 'undefined' || !document.fonts?.check) return true;
  try {
    return document.fonts.check(`12px "${option.probe}"`);
  } catch {
    // A malformed shorthand throws rather than returning false. Treat an
    // unanswerable probe as available - the stack falls back on its own.
    return true;
  }
}

/* ---------- applying ---------- */

/**
 * Paint the choice onto <html>.
 *
 * JS writes only the four values that needed a contrast comparison; globals.css
 * owns everything downstream of `--primary` and the choice between the light
 * and dark variants, which is why flipping the theme recomputes nothing here.
 * `data-accent` is the flag those rules key on, so an app with no custom accent
 * keeps the hand-tuned palette byte for byte.
 */
export function applyAppearance({ accent, fontId }: Appearance) {
  const root = document.documentElement;

  const hex = normalizeAccent(accent);
  if (hex) {
    const vars = accentVars(hex);
    root.dataset.accent = 'custom';
    root.style.setProperty('--accent-h', vars.hue);
    root.style.setProperty('--accent-light', vars.light);
    root.style.setProperty('--accent-on-light', vars.onLight);
    root.style.setProperty('--accent-dark', vars.dark);
    root.style.setProperty('--accent-on-dark', vars.onDark);
  } else {
    delete root.dataset.accent;
    for (const v of ['--accent-h', '--accent-light', '--accent-on-light', '--accent-dark', '--accent-on-dark']) {
      root.style.removeProperty(v);
    }
  }

  const font = fontId && fontId !== DEFAULT_FONT_ID ? fontById(fontId) : null;
  // `--font-mono` is deliberately left alone: asset codes and env var names must
  // stay monospaced whatever the UI face is.
  if (font) root.style.setProperty('--font-sans', font.stack);
  else root.style.removeProperty('--font-sans');
}

/* ---------- storage ---------- */

/** What the no-flash script in index.html replays. It caches the DERIVED paint
 *  values next to the raw choice on purpose: the script is then a dumb replayer
 *  of "whatever we painted last time" rather than a second copy of the contrast
 *  rule and the font table, neither of which it could import. */
type CachedAppearance = Appearance & { vars: AccentVars | null; fontStack: string | null };

export function readStoredAppearance(): Appearance {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) return { accent: null, fontId: null };
    const parsed = JSON.parse(raw) as Partial<CachedAppearance>;
    return {
      accent: normalizeAccent(parsed.accent),
      fontId: typeof parsed.fontId === 'string' ? parsed.fontId : null,
    };
  } catch {
    // Private browsing, or a cache written by an older shape.
    return { accent: null, fontId: null };
  }
}

function storeAppearance({ accent, fontId }: Appearance) {
  try {
    const hex = normalizeAccent(accent);
    const font = fontId && fontId !== DEFAULT_FONT_ID ? fontById(fontId) : null;
    if (!hex && !font) {
      localStorage.removeItem(APPEARANCE_STORAGE_KEY);
      return;
    }
    const payload: CachedAppearance = {
      accent: hex,
      vars: hex ? accentVars(hex) : null,
      fontId: font ? font.id : null,
      fontStack: font ? font.stack : null,
    };
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Non-fatal: the choice still applies for this session.
  }
}

/**
 * Read/write hook for the Appearance panel. No context provider because there
 * is exactly one consumer - the account menu. Add one if a second appears, or
 * two panels will disagree about what is selected.
 *
 * Deliberately does NOT apply on mount: this hook lives inside a Radix
 * dropdown, which mounts when the menu is opened rather than when the app
 * loads, so an effect here would be an effect on "menu first opened". main.tsx
 * owns the load-time apply.
 */
export function useAppearance() {
  const [appearance, setState] = React.useState<Appearance>(readStoredAppearance);

  const setAppearance = React.useCallback((next: Appearance) => {
    setState(next);
    applyAppearance(next);
    storeAppearance(next);
  }, []);

  return { appearance, setAppearance };
}
